import * as d3 from 'd3';
import moment from 'moment';
import Filters from '../../constants/filters'
import LoeEventsModule from './modules/LoeEventsModule'
import LinesStateModule from './modules/LinesStateModule'
import FicBicModule from './modules/FicBicModule'

import coloringUtil from '../../utils/coloringUtil';
import newMarket from '../../assets/images/new-market.svg';
import newIndication from '../../assets/images/new-indication.svg';
import stoppedRevenue from '../../assets/images/stopped_at.svg';
import newLoe from '../../assets/images/new-loe.svg';
import newXLoe from '../../assets/images/new-x-axis-loe.svg';
import store from '../../store';

class BaseDrawingService {
    constructor(selector, chart, type, cumulativeType) {
        return (async () => {
            this.eventDate = new Map();
            this.selector = selector;
            this.type = type;
            this.data = chart.data;
            this.cumulativeData =(chart.cumulativeData!==undefined)? chart.cumulativeData:null;
            this.ficBicData = (chart.ficBicData !== undefined)? chart.ficBicData : null;
            this.showCumulativeRevenue = this.cumulativeData != null;
            this.margins = { top: 0, right:  this.showCumulativeRevenue? 80:40, bottom: 65, left: 40 };
            this.events = chart.events || [];
            this.filters = chart.filters || null;
            this.cumulative = cumulativeType || 'none';
            this.showLoe = (this.filters.showLoe!==undefined)?this.filters.showLoe:false;
            this.colorByFicBic = (this.filters.colorByFicBic !== undefined)? this.filters.colorByFicBic : false;
            this.loeEvents = LoeEventsModule.getLoeEvents(this);
            this.quarters = this.getQuarters(this.data);
            this.scaleLogType = (this.filters.scaleLogType!==undefined)?this.filters.scaleLogType:true;
            this.showStopIcon = (this.filters.showStopIcon!==undefined)?this.filters.showStopIcon:true;
            this.chartId = chart.chartId;
            this.linesState = {
                ...LinesStateModule.initState(this.data, this.showCumulativeRevenue),
                ...this.filters.linesState
            }

            if (chart.x_domain_low != null && chart.x_domain_high != null) {
                this.xDomain = [chart.x_domain_low, chart.x_domain_high];
            }

            if (chart.y_domain_low != null && chart.y_domain_high != null) {
                this.yDomain = [0, chart.y_domain_high];
            }

            this.xScale = d3.scaleLinear();

            if (this.scaleLogType) {
                this.changeToLogScale();

            } else {
                this.changeToLinearScale();
            }
            this.xAxisFunction = d3.axisBottom();
            this.xYearAxisFunction = d3.axisBottom();
            this.yAxisFunction = d3.axisRight();
            this.yAxisLeftFunction = d3.axisLeft();

            this.lineFunction = d3.line().curve(d3.curveMonotoneX)
                .x(d =>{
                    return d.xFactor != undefined ? 
                        this.xScale(this.getQuarterIndex(d) + d.xFactor) 
                        : this.xScale(this.getQuarterIndex(d));
                })
                .y(d =>{
                    return d.yFactor != undefined ?
                        this.yScale(d.revenue*1 + d.yFactor)
                        : this.yScale(d.revenue)
                });

            if(this.showCumulativeRevenue){
                this.yCumulativeAxisRightFunction = d3.axisRight();
                this.cumulativeLineFunction = d3.line().curve(d3.curveMonotoneX)
                            .x(d => this.xScale(this.getQuarterIndex(d)))
                            .y(d => this.yCumulativeScale(d.revenue));
            }

            const marketIconData = await d3.xml(newMarket);
            this.marketIcon = d3.select(marketIconData.documentElement).select('.UI-Kit');

            const indicationIconData = await d3.xml(newIndication);
            this.indicationIcon = d3.select(indicationIconData.documentElement).select('.UI-Kit');


            const stoppedIconData = await d3.xml(stoppedRevenue);
            this.stoppedIcon = d3.select(stoppedIconData.documentElement).select('.UI-Kit');

            
            const loeIconData = await d3.xml(newLoe);
            this.loeIcon = d3.select(loeIconData.documentElement).select('.UI-Kit');
            this.loeIconBBox = {
                width: loeIconData.firstChild.attributes.width.nodeValue * 1,
                height: loeIconData.firstChild.attributes.height.nodeValue * 1,
            }; 

            const xLoeIconData = await d3.xml(newXLoe);
            this.xLoeIcon = d3.select(xLoeIconData.documentElement).select('.UI-Kit');
            this.xLoeIconBBox = {
                width: xLoeIconData.firstChild.attributes.width.nodeValue * 1,
                height: xLoeIconData.firstChild.attributes.height.nodeValue * 1,
            };
     

            return this;
        })();
    }
    changeToLogScale(){
        this.yScale = d3.scaleSqrt()
            .domain([0, 100, 500, 1000, 5000])
            .range([0, 300, 400, 500, 600]);
        
        if(this.showCumulativeRevenue)
            this.yCumulativeScale = d3.scaleSqrt()
                .domain([0, 100, 500, 1000, 5000])
                .range([0, 300, 400, 500, 600]);
    };

    changeToLinearScale(){
        this.yScale = d3.scaleLinear();
        
        if(this.showCumulativeRevenue)
            this.yCumulativeScale = d3.scaleLinear();
    }

    refreshScale(){
        this.setDefaultDomain();
        this.setScale();
        this.drawAxes();
        this.drawData();
    }

    getPreviousQuarter(currentQuarter) {
        if (!currentQuarter) {
            return currentQuarter;
        }

        const year = parseInt(currentQuarter.split(' ')[0], 10);
        const quarter = currentQuarter.split(' ')[1];
        let previousQuarter;

        if (quarter === 'Q1') {
            previousQuarter = `${year - 1} Q4`;
        } else {
            previousQuarter = `${year} Q${parseInt(quarter.charAt(1), 10) - 1}`;
        }

        return previousQuarter;
    }

    getNextQuarter(currentQuarter) {
        if (!currentQuarter) {
            return currentQuarter;
        }

        const year = parseInt(currentQuarter.split(' ')[0], 10);
        const quarter = currentQuarter.split(' ')[1];
        let nextQuarter;

        if (quarter === 'Q4') {
            nextQuarter = `${year + 1} Q1`;
        } else {
            nextQuarter = `${year} Q${parseInt(quarter.charAt(1), 10) + 1}`;
        }

        return nextQuarter;
    }

    getDataQuarters(data){
        let quarters = [];

        if(this.filters.startSyncView){
            const allMaxIndexedQuerters = Object.keys(data).map(dataKey => d3.max(this.data[dataKey], d => d.indexedQuarter));
            const maxIndexedQuerter = d3.max(allMaxIndexedQuerters, d => d);
            quarters = Array.from(Array(maxIndexedQuerter+1).keys()).map((e)=> e.toString());
            return quarters;
        }

        
        const allQuarters = Object.keys(data).reduce((array, dataKey) => array.concat(data[dataKey]), [])
            .map(datapoint => `${datapoint.year} ${datapoint.quarter}`)
            .filter((datapoint, index, array) => array.indexOf(datapoint) === index)
            .sort((a, b) => {
                if (a > b) {
                    return 1;
                }

                if (a < b) {
                    return -1;
                }

                return 0;
            });

        const firstQuarter = this.getPreviousQuarter(this.getPreviousQuarter(allQuarters[0]));
        let lastQuarter = this.getNextQuarter(this.getNextQuarter(allQuarters[allQuarters.length - 1]));
        
        let currentQuarter = firstQuarter;

        while (currentQuarter !== this.getNextQuarter(lastQuarter)) {
            quarters.push(currentQuarter);
            currentQuarter = this.getNextQuarter(currentQuarter);
        }

        return quarters;
    }

    getQuarters(data, forceLoeQuarters =false) {
        const allQuarters = this.getDataQuarters(data);
        if(!this.showLoe && !forceLoeQuarters) return allQuarters;

        let quarters = [];

        
        let firstQuarter = this.getPreviousQuarter(this.getPreviousQuarter(allQuarters[0]));
        let lastQuarter = this.getNextQuarter(this.getNextQuarter(allQuarters[allQuarters.length - 1]));

        
        const loeQuarters = LoeEventsModule.getQuarters(this);
        const firstLoeQuarter = this.getPreviousQuarter(this.getPreviousQuarter(loeQuarters[0]));
        const lastLoeQuarter = this.getNextQuarter(this.getNextQuarter(loeQuarters[loeQuarters.length - 1]));

        if(firstLoeQuarter != null && this.compareQuarters(firstLoeQuarter, firstQuarter) >= 1){
            firstQuarter = firstLoeQuarter;
        }
        
        if(lastLoeQuarter != null && this.compareQuarters(lastQuarter,lastLoeQuarter) >= 1){
            lastQuarter = lastLoeQuarter;
        }
        

        let currentQuarter = firstQuarter;


        while (currentQuarter !== this.getNextQuarter(lastQuarter)) {
            quarters.push(currentQuarter);
            currentQuarter = this.getNextQuarter(currentQuarter);
        }

        return quarters;
    }

    toggleColorByFicBic(){
        this.colorByFicBic = !this.colorByFicBic;

        this.forceRedraw();
    }

    toggleShowLoe(){
        this.showLoe = !this.showLoe;

        const newQuarters = this.getQuarters(this.data, true);

        this.forceRedraw(newQuarters);
    }

    handleLoeEventsUpdated(newChartEvents){
        this.events = newChartEvents;
        this.loeEvents = LoeEventsModule.getLoeEvents(this);

        const updatedQuarters = this.getQuarters(this.data)

        const newQuarters = Array.from(new Set([...this.quarters, ...updatedQuarters]))
            .sort(this.compareQuarters)
            .reverse();

        this.forceRedraw(newQuarters);
    }

    compareQuarters(a, b){
        const aQuarterInfo = {
            year: a.split(' ')[0],
            quarter: a.split(' ')[1],
        }; 

        const bQuarterInfo = {
            year: b.split(' ')[0],
            quarter: b.split(' ')[1],
        };

        if(aQuarterInfo.year > bQuarterInfo.year)
            return -1; 
        if(aQuarterInfo.year < bQuarterInfo.year)
            return 1;
        
        if(aQuarterInfo.quarter === bQuarterInfo.quarter)
            return 0;
        
        return aQuarterInfo.quarter > bQuarterInfo.quarter ? -1 : 1;
    }

    getQuarterIndex(data) {
        if (data.year === "undefined" || data.quarter === "undefined") return -1;

        if(this.filters.startSyncView){
            return data.indexedQuarter;
        }

        return this.quarters.findIndex(quarter => quarter === `${parseInt(data.year)} ${data.quarter}`);
    }

    getTickFormat(index) {
        if (this.xDomain[1] - this.xDomain[0] < 24) {
            return 'normal';
        }

        if (this.quarters[index].indexOf('Q1') >= 0) {
            return 'year';
        }

        return 'dot';
    }

    xAxisVisibility(xAxis) {
        const that = this;

        xAxis
            .selectAll('.tick')
            .attr('class', function opacityFunction() {
                const tickElement = d3.select(this);
                const translateX = parseFloat(tickElement.attr('transform').split('translate(')[1].split(',')[0]);

                if (translateX > that.svg.width + 1 || translateX < -1) {
                    tickElement.classed('hidden', true);
                } else {
                    tickElement.classed('hidden', false);
                }

                if (translateX > that.svg.width + 0.4 || translateX < 0.6) {
                    tickElement.classed('hide-line', true);
                } else {
                    tickElement.classed('hide-line', false);
                }

                return tickElement.attr('class');
            });
    }

    pan(direction) {
        if (direction === 'left' && this.xDomain[0] > 0) {
            this.xDomain[0] -= 1;
            this.xDomain[1] -= 1;
        } else if (direction === 'right' && this.xDomain[1] < this.quarters.length - 1) {
            this.xDomain[0] += 1;
            this.xDomain[1] += 1;
        }

        this.redraw(true);
    }

    drag() {
        let dragStartX = 0;
        let dragStartY = 0;

        d3.select(`#${this.selector}`)
            .call(
                d3.drag()
                    .on('start', () => {
                        dragStartX = d3.event.x;
                        dragStartY = d3.event.y;
                    })
                    .on('drag', () => {
                        const rangeX = this.xScale.range()[1] - this.xScale.range()[0];
                        const rangeY = this.yScale.range()[1] - this.yScale.range()[0];
                        const rangeCumulativeY = this.showCumulativeRevenue ? this.yCumulativeScale.range()[1] - this.yCumulativeScale.range()[0] : null;
                        const domainX = this.xDomain[1] - this.xDomain[0];
                        const domainY = this.yDomain[1] - this.yDomain[0];
                        const domainCumulativeY = this.showCumulativeRevenue ? this.cumulativeYDomain[1] - this.cumulativeYDomain[0] : null;
                        const directionX = d3.event.x < dragStartX ? 'left' : 'right';
                        const directionY = d3.event.y < dragStartY ? 'up' : 'down';
                        const deltaX = Math.abs(dragStartX - d3.event.x);
                        const deltaY = Math.abs(dragStartY - d3.event.y);

                        if (directionY === 'up' && this.yDomain[0] > 0 && ( !this.showCumulativeRevenue ||  this.cumulativeYDomain[0] > 0)) {

                            this.yDomain[0] += deltaY / rangeY * domainY;
                            if(this.showCumulativeRevenue)
                                this.cumulativeYDomain[0] += deltaY / rangeCumulativeY * domainCumulativeY;

                            // do not allow a starting y value less than 0
                            if (this.yDomain[0] < 0) {
                                this.yDomain[0] = 0;
                            }

                            if (this.showCumulativeRevenue && this.cumulativeYDomain[0] < 0) {
                                this.cumulativeYDomain[0] = 0;
                            }

                            this.yDomain[1] += deltaY / rangeY * domainY;

                            if(this.showCumulativeRevenue)
                                this.cumulativeYDomain[1] += deltaY / rangeCumulativeY * domainCumulativeY;
                        } else if (
                            directionY === 'down' && this.yDomain[1] < this.defaultYDomain[1] 
                            && (!this.showCumulativeRevenue || this.cumulativeYDomain[1] < this.defaultCumulativeYDomain[1])
                        ) {
                            this.yDomain[0] -= deltaY / rangeY * domainY;
                            this.yDomain[1] -= deltaY / rangeY * domainY;
                            if(this.showCumulativeRevenue){
                                this.cumulativeYDomain[0] -= deltaY / rangeCumulativeY * domainCumulativeY;
                                this.cumulativeYDomain[1] -= deltaY / rangeCumulativeY * domainCumulativeY;
                            }                      

                        }

                        dragStartY = d3.event.y;

                        if (directionX === 'right' && this.xDomain[0]-2 > 0) {
                            this.xDomain[0] -= deltaX / rangeX * domainX;
                            this.xDomain[1] -= deltaX / rangeX * domainX;
                        } else if (directionX === 'left' && this.xDomain[1] < this.quarters.length - 3) {
                            this.xDomain[0] += deltaX / rangeX * domainX;
                            this.xDomain[1] += deltaX / rangeX * domainX;
                        }

                        dragStartX = d3.event.x;

                        this.redraw();
                    })
                    .on('end', () => {
                        this.xDomain[0] = Math.round(this.xDomain[0]) < 0 ? 0 : Math.round(this.xDomain[0]);
                        this.xDomain[1] = Math.round(this.xDomain[1]) > this.quarters.length-1 ?  this.quarters.length-1 : Math.round(this.xDomain[1]);

                        this.redraw(true);
                    }),
            );
    }

    xAxisZoom(direction) {
        if (direction === 'out' && this.xDomain[0] < this.xDomain[1] - 2) {
            //check if its an odd number
            const odd = (this.xDomain[1]-this.xDomain[0]+1)%2;

            if(odd){
                this.xDomain[0] += 1;
            }else{
                this.xDomain[0] += 1;
                this.xDomain[1] -= 1;
            }

        } else  if (direction === 'out' && this.xDomain[0] < this.xDomain[1] - 1) {
            this.xDomain[0] += 1;
            // this.xDomain[1] -= 1;
        } else if (direction === 'in') {

            if (this.xDomain[0]-2 > 0) {
                this.xDomain[0] -= 1;
            }

            if (this.xDomain[1] < this.quarters.length - 3) {
                this.xDomain[1] += 1;
            }
        }

        this.redraw(true);
    }

    yAxisZoom() {
        this.svg.element
            .append('rect')
            .attr('class', 'vertical-dragger')
            .attr('x', this.svg.width)
            .attr('y', 0)
            .attr('width', 50)
            .attr('height', this.svg.height);

        let dragStartY = 0;

        this.svg.element.select('.vertical-dragger')
            .call(
                d3.drag()
                    .on('start', () => {
                        dragStartY = d3.event.y;
                    })
                    .on('drag', () => {
                        const range = this.yScale.range()[1] - this.yScale.range()[0];
                        const rangeCumulative = this.showCumulativeRevenue ? this.yCumulativeScale.range()[1] - this.yCumulativeScale.range()[0] : null;
                        const domain = this.yDomain[1] - this.yDomain[0];
                        const domainCumulative = this.showCumulativeRevenue ? this.cumulativeYDomain[1] - this.cumulativeYDomain[0] : null;
                        const direction = d3.event.y < dragStartY ? 'up' : 'down';
                        const deltaY = Math.abs(dragStartY - d3.event.y) / range * domain;
                        const deltaCumulativeY = this.showCumulativeRevenue ? Math.abs(dragStartY - d3.event.y) / rangeCumulative * domainCumulative : null;

                        if (
                            direction === 'up'
                            && this.yDomain[1] < this.defaultYDomain[1]
                            && this.yDomain[0] > this.defaultYDomain[0]
                            && (!this.showCumulativeRevenue 
                                || (this.cumulativeYDomain[1] < this.defaultCumulativeYDomain[1]
                                    && this.cumulativeYDomain[0] > this.defaultCumulativeYDomain[0])
                            )
                        ) {
                            this.yDomain[0] += deltaY;
                            this.yDomain[1] -= deltaY;

                            if(this.showCumulativeRevenue){
                                this.cumulativeYDomain[0] += deltaCumulativeY;
                                this.cumulativeYDomain[1] -= deltaCumulativeY;
                            }

                        } else if (direction === 'down') {
                            this.yDomain[0] -= deltaY;
                            this.yDomain[1] += deltaY;

                            if(this.showCumulativeRevenue){
                                this.cumulativeYDomain[0] -= deltaCumulativeY;
                                this.cumulativeYDomain[1] += deltaCumulativeY;
                            }
                        }

                         // do not allow a starting y value less than 0
                        if (this.yDomain[0] < 0) {
                            this.yDomain[0] = 0;
                        }
                        if (this.showCumulativeRevenue && this.cumulativeYDomain[0] < 0) {
                            this.cumulativeYDomain[0] = 0;
                        }

                        dragStartY = d3.event.y;
                        this.redraw();
                    }),
            );
    }

    svgEvents() {
        d3.select(`#${this.selector}`)
            .select('svg')
            .on('click', () => {
                this.lineClickEvent();
            });
    }

    wheelEvents() {
        d3.select(`#${this.selector}`)
            .on('wheel', () => {
                d3.event.preventDefault();
                this.xAxisZoom(d3.event.deltaY >= 0 ? 'in' : 'out');
            });
    }

    keyboardEvents() {
        d3.select('body')
            .on('keydown', () => {
                if (d3.event.key === 'ArrowLeft') {
                    this.pan('left');
                } else if (d3.event.key === 'ArrowRight') {
                    this.pan('right');
                } else if (d3.event.key === 'Escape') {
                    this.lineClickEvent();
                }
            });
    }

    handleLinesStateUpdate = () => LinesStateModule.handleLinesStateUpdate(this);

    lineClickEvent (id, entity, entityName){
        LinesStateModule.lineClickEvent(this, id, entity, entityName)
    };

    bindEvents() {
        this.svgEvents();
        this.wheelEvents();
        this.keyboardEvents();
        this.drag();
        this.yAxisZoom();
    }

    setDefaultDomain(force) {
        const dataKeys = Object.keys(this.data);
        let dataQuartersLength = this.quarters.length;
        let firstDataQuarterIndex = 0;

        const dataQuarters = this.getDataQuarters(this.data);
        firstDataQuarterIndex = this.quarters.indexOf(dataQuarters[0]);
        dataQuartersLength = firstDataQuarterIndex + dataQuarters.length;

        const xDomainLow = this.filters.startSyncView ? firstDataQuarterIndex : firstDataQuarterIndex + 2;
        const xDomainHigh = this.filters.startSyncView ? dataQuartersLength - 1 : dataQuartersLength - 3;

        if (!this.xDomain || force) {
            this.xDomain = [xDomainLow, xDomainHigh];
            this.xYearDomain = [xDomainLow, (dataQuartersLength - 3)/4];
        }

        const allMaxYValues = dataKeys.map(dataKey => d3.max(this.data[dataKey], d => parseFloat(d.revenue)));
        const allMinYValues = dataKeys.map(dataKey => d3.min(this.data[dataKey], d => parseFloat(d.revenue)));

        const maxY = d3.max(allMaxYValues, d => d);
        const minY = d3.min(allMinYValues, d => d);

        let minValueByType = minY - ((maxY - minY) * 0.2);

        // Only for this chart types
        if (this.type === Filters.chartTypes[1].value || this.filters.breakApart || this.filters.viewCompetitors) {
            minValueByType = 0;
        }
        // we round up the maximum domain to the nearest 100 amount

        this.defaultYDomain = [minValueByType, maxY *1.05]; // 1.05 as a "top padding"


        if (!this.yDomain || force) {
            this.yDomain = [0, this.defaultYDomain[1]];
        }

        if(this.showCumulativeRevenue){
            // set yCumulativeAxis domain

            const cumulativeMaxY = d3.max(this.cumulativeData, d => parseFloat(d.revenue));
            const cumulativeMinY = d3.min(this.cumulativeData, d => parseFloat(d.revenue));

            let minCumulativeValueByType = cumulativeMinY - ((cumulativeMaxY - cumulativeMinY) * 0.2);

            // Only for this chart types
            if (this.type === Filters.chartTypes[1].value || this.filters.breakApart || this.filters.viewCompetitors) {
                minCumulativeValueByType = 0;
            }
            // we round up the maximum domain to the nearest 100 amount

            this.defaultCumulativeYDomain = [minCumulativeValueByType, cumulativeMaxY *1.05]; // 1.05 as a "top padding"


            if (!this.cumulativeYDomain || force) {
                this.cumulativeYDomain = [0, this.defaultCumulativeYDomain[1]];
            }
        }
    }

    setScale() {
        if(this.svg) {
            this.xScale
                .range([0, this.svg.width])
                .domain([this.xDomain[0], this.xDomain[1]]);

            if(this.yDomain[0] < 0){
                this.yDomain[0] = 0;
            }

            this.yScale
                .range([this.svg.height, 0])
                .domain([this.yDomain[0], this.yDomain[1]]);
            
            if(this.showCumulativeRevenue)
                this.yCumulativeScale
                    .range([this.svg.height, 0])
                    .domain([this.cumulativeYDomain[0], this.cumulativeYDomain[1]]);
        }
    }

    drawAxes(animate) {
        const that = this;

        this.xAxisFunction
            .scale(this.xScale)
            .tickFormat((d) => {
                return this.filters.startSyncView? d : this.quarters[d].split(' ')[1];
            })
            .tickValues(this.quarters.map((quarter, index) => index))
            .tickSize(-this.svg.height);

        this.xYearAxisFunction
            .scale(this.xScale)
            .tickFormat((d) => {
                // Add the year to the second axis, make adjustments if the range is incomplete
                // either at the start or the beginning

                const firstQuarter = that.quarters[that.xDomain[0]];
                const lastQuarter = that.quarters[that.xDomain[1]];
                const currentQuarter = that.quarters[d];

                // just to avoid glitch jump
                if (currentQuarter === this.getPreviousQuarter(firstQuarter) ||
                    currentQuarter === this.getNextQuarter(lastQuarter)) {
                    return ' ';
                }

                if (currentQuarter.split(' ')[1] == 'Q3' && this.getPreviousQuarter(currentQuarter) === firstQuarter) {
                    return currentQuarter.split(' ')[0];
                }

                if ((currentQuarter.split(' ')[1] == 'Q3' || currentQuarter.split(' ')[1] == 'Q4')
                    && currentQuarter == firstQuarter) {
                    return currentQuarter.split(' ')[0];
                }

                if ((currentQuarter.split(' ')[1] == 'Q1' &&
                    (this.getNextQuarter(currentQuarter) === lastQuarter || currentQuarter === lastQuarter) ||
                    (currentQuarter.split(' ')[1] == 'Q2' && currentQuarter !== lastQuarter && currentQuarter !== firstQuarter))) {
                    return currentQuarter.split(' ')[0];
                }

                return ' ';
            })
            .tickValues(this.quarters.map((quarter, index) => index))
        ;

        this.yAxisFunction
            .scale(this.yScale)
            .tickSize(-this.svg.width);

        this.yAxisLeftFunction
            .scale(this.yScale)
            .tickSize(0);
        
        if(this.showCumulativeRevenue)
            this.yCumulativeAxisRightFunction
                .scale(this.yCumulativeScale)
                .tickFormat(d => {
                    if (d < 1000) return d.toString();
                    return `${d / 1000}k`;
                })
                .tickSize(0);    


        let xAxis;
        let xYearAxis;
        let yAxis;
        let yLeftAxis;
        let yCumulativeRightAxis;

        // Draw the y-axis

        if (this.svg.element.select('.y-axis').empty()) {
            yAxis = this.svg.element
                .append('g')
                .attr('class', 'axis y-axis');
        } else {
            yAxis = this.svg.element.select('.y-axis');
        }

        if (animate) {
            yAxis = yAxis
                .transition()
                .duration(500);
        }

        yAxis
            .attr('transform', `translate(${this.svg.width}, 0)`)
            .call(this.yAxisFunction)
            .selectAll('.tick text')
            .attr('dx', '0.75em');

        // Draw the new left y-axis

        if (this.svg.element.select('.y-left-axis').empty()) {
            yLeftAxis = this.svg.element
                .append('g')
                .attr('class', 'axis y-left-axis');
        } else {
            yLeftAxis = this.svg.element.select('.y-left-axis');
        }

        if (animate) {
            yLeftAxis = yLeftAxis
                .transition()
                .duration(500);
        }

        yLeftAxis
            .call(this.yAxisLeftFunction);

        if(this.showCumulativeRevenue){
            // Draw the Cumulative right y-axis

            if (this.svg.element.select('.y-cumulative-axis').empty()) {
                yCumulativeRightAxis = this.svg.element
                    .append('g')
                    .attr('class', 'axis y-cumulative-axis');
            } else {
                yCumulativeRightAxis = this.svg.element.select('.y-cumulative-axis');
            }

            if (animate) {
                yCumulativeRightAxis = yCumulativeRightAxis
                    .transition()
                    .duration(500);
            }

            yCumulativeRightAxis
                .attr('transform', `translate(${this.svg.width + 40}, 0)`)
                .call(this.yCumulativeAxisRightFunction)
                .selectAll('.tick text')
                .attr('dx', '0.75em');
        }

        // Draw the x-axis

        if (this.svg.element.select('.x-axis').empty()) {
            xAxis = this.svg.element
                .append('g')
                .attr('class', 'axis x-axis');

            this.svg.element
                .append('line')
                .attr('class', 'hover-line x-hover-line');

            this.svg.element
                .append('line')
                .attr('class', 'hover-line y-hover-line');

            // Draw decorative circle at bottom of each tick on xaxis
            xAxis
                .call(this.xAxisFunction)
                .selectAll('.tick').append("circle")
                .attr("cx", 0)
                .attr("cy", 0)
                .attr("r", 2)
                .style("fill", "rgb(215, 215, 215)");

            const xHoverRect = this.svg.element
                .append('g')
                .attr('class', 'hover-rect x-hover-rect')
                .attr('visibility', 'hidden');

            xHoverRect
                .append('rect')
                .attr('width', 50)
                .attr('height', 20)
                .attr('rx', 5)
                .attr('ry', 5);

            xHoverRect.append('text');

            const yHoverRect = this.svg.element
                .append('g')
                .attr('class', 'hover-rect y-hover-rect')
                .attr('visibility', 'hidden');

            yHoverRect
                .append('rect')
                .attr('width', 40)
                .attr('height', 20)
                .attr('rx', 5)
                .attr('ry', 5);

            yHoverRect.append('text');
        } else {
            xAxis = this.svg.element.select('.x-axis');
        }
        if (animate) {
            xAxis = xAxis
                .transition()
                .duration(500)
                .tween('xAxisVisibility', () => function opacityFunction() {
                    that.xAxisVisibility(d3.select(this));
                })
                .on('end', function opacityFunction() {
                    that.xAxisVisibility(d3.select(this));
                });
        } else {
            this.xAxisVisibility(xAxis);
        }

        xAxis
            .attr('transform', `translate(0, ${this.svg.height})`)
            .call(this.xAxisFunction)
            .selectAll('.tick text')
            .attr('dy', '2em');

        // make dashed ox axes
        xAxis.call(g => g.selectAll(".tick:not(:first-of-type) line")
        .attr("stroke-opacity", 0.7)
        .attr("stroke-dasharray", "5,5"));

        // Draw the x-axis that shows the YEAR -xYearAxis-
        if(!this.filters.startSyncView){
            if (this.svg.element.select('.x-year-axis').empty()) {
                xYearAxis = this.svg.element
                    .append('g')
                    .attr('class', 'axis x-year-axis');
    
            } else {
                xYearAxis = this.svg.element.select('.x-year-axis');
            }
    
            if (animate) {
                xYearAxis = xYearAxis
                    .transition()
                    .duration(500)
                    .tween('xAxisVisibility', () => function opacityFunction() {
                        that.xAxisVisibility(d3.select(this));
                    })
                    .on('end', function opacityFunction() {
                        that.xAxisVisibility(d3.select(this));
                    });
            } else {
                this.xAxisVisibility(xYearAxis);
            }
    
            // calculate the offset necessary to center the label between two ticks
            const xOffset =    (this.svg.width/ (this.xDomain[1]- this.xDomain[0]))/2;
    
            xYearAxis
                .attr('transform', `translate(0, ${this.svg.height+40})`)
                .call(this.xYearAxisFunction)
                .selectAll('.tick text')
                .attr('dy', '0')
                .attr('dx', function ( d ) {
                    const firstQuarter = that.quarters[that.xDomain[0]];
                    const lastQuarter = that.quarters[that.xDomain[1]];
                    const currentQuarter = that.quarters[d];
    
                    //adjust the offset of the years tag depending on how many years on the range
                    //are visible in order to center it
    
                    if ((currentQuarter.split(' ')[1] == 'Q4' && currentQuarter === firstQuarter) ||
                        (currentQuarter.split(' ')[1] == 'Q2' && currentQuarter === firstQuarter) ||
                        (currentQuarter.split(' ')[1] == 'Q3' && that.getPreviousQuarter(currentQuarter) === firstQuarter)) {
                        return 0;
                    }
    
                    if ((currentQuarter.split(' ')[1] == 'Q1' && currentQuarter === lastQuarter) ||
                        (currentQuarter.split(' ')[1] == 'Q2' && that.getNextQuarter(currentQuarter) === lastQuarter)) {
                        return 0;
                    }
    
                    return xOffset;
                });    
        }

        
        // make dashed ox axes
        xAxis.call(g => g.selectAll(".tick:not(:first-of-type) line")
        .attr("stroke-opacity", 0.7)
        .attr("stroke-dasharray", "5,5"));
        // make dashed ox axes
        if(!this.filters.startSyncView){
            xYearAxis.call(g => g.selectAll("line").style('display','none'));
            xYearAxis.call(g => g.selectAll("line").style('opacity','0'));
            xYearAxis.call(g => g.selectAll("line").attr("opacity", 0));
            xYearAxis.call(g => g.selectAll("line").attr("display",'none'));
    
            xYearAxis.call(g => g.selectAll("path").style('display','none'));
            xYearAxis.call(g => g.selectAll("path").style('opacity','0'));
            xYearAxis.call(g => g.selectAll("path").attr("opacity", 0));
            xYearAxis.call(g => g.selectAll("path").attr("display",'none'));
        }
       

        // make dashed oy axes
        yAxis.call(g => g.selectAll(".tick:not(:first-of-type) line")
        .attr("stroke-opacity", 0.7)
        .attr("stroke-dasharray", "5,5"));
    }

    dataLineHover(data ,isCumulative=false) {
        const xPos = this.xScale(this.getQuarterIndex(data));
        const yPos = isCumulative ? this.yCumulativeScale(data.revenue) : this.yScale(data.revenue);

        const xHoverLine = d3.select(`#${this.selector}`).select('.x-hover-line');
        const yHoverLine = d3.select(`#${this.selector}`).select('.y-hover-line');
        const xHoverRect = d3.select(`#${this.selector}`).select('.x-hover-rect');
        const yHoverRect = d3.select(`#${this.selector}`).select('.y-hover-rect');

        xHoverLine
            .attr('visibility', 'visible')
            .attr('x1', xPos)
            .attr('y1', 0)
            .attr('x2', xPos)
            .attr('y2', this.svg.height);

        
        if(!this.filters.startSyncView){
            xHoverRect
                .attr('visibility', 'visible')
                .attr('transform', `translate(${xPos - 25}, ${this.svg.height + 8})`);

            xHoverRect.select('text')
                .html(`${data.year} ${data.quarter}`);
        }

        if(isCumulative){
            yHoverLine
                .attr('visibility', 'visible')
                .attr('x1', 0)
                .attr('y1', yPos)
                .attr('x2', this.svg.width+40)
                .attr('y2', yPos);
            yHoverRect
                .attr('visibility', 'visible')
                .attr('transform', `translate(${this.svg.width + 40}, ${yPos - 10})`);
        } else {
            yHoverLine
                .attr('visibility', 'visible')
                .attr('x1', 0)
                .attr('y1', yPos)
                .attr('x2', this.svg.width)
                .attr('y2', yPos);

            yHoverRect
                .attr('visibility', 'visible')
                .attr('transform', `translate(${this.svg.width}, ${yPos - 10})`);
        }

        yHoverRect.select('text')
            .html(parseFloat(data.revenue).toFixed(2));
    }

    dataLineHoverEnd() {
        const xHover = d3.select(`#${this.selector}`).select('.x-hover-line');
        const yHover = d3.select(`#${this.selector}`).select('.y-hover-line');
        const xHoverRect = d3.select(`#${this.selector}`).select('.x-hover-rect');
        const yHoverRect = d3.select(`#${this.selector}`).select('.y-hover-rect');

        xHover.attr('visibility', 'hidden');
        yHover.attr('visibility', 'hidden');
        xHoverRect.attr('visibility', 'hidden');
        yHoverRect.attr('visibility', 'hidden');
    }

    getUserFicBicColors(){
        const { fic_color, bic_color, fic_bic_color } = store.getters["setting/getAll"];
        return {fic_color, bic_color, fic_bic_color};
    }

    drawData(animate, force = false) {
        const data = this.parseData(this.data);
        const cumulativeData = this.cumulativeData;
        const lineColors = coloringUtil.getAllLinesColors(this.data, this.type);
        const userFicBicColors = this.getUserFicBicColors();

        this.loeEventDateMap = new Map;
        let hasLines = true;
        const self = this;

        if (!this.lines) {
            this.lines = this.svg.element
                .append('g')
                .attr('class', 'lines')
                .attr('clip-path', `url(#clip-${this.selector})`);

            hasLines = false;
        }

        Object.keys(data).forEach((dataKey) => {
            let line;
            let paths;
            let circles;
            let stoppedDots;

            if (!hasLines) {
                let hasFicBicStatus = false;

                if(this.colorByFicBic && this.ficBicData != null){
                    const brandId = this.data[dataKey][0].drug_brand_id;
                    hasFicBicStatus = FicBicModule.checkHasFicBicStatus(this, brandId); 
                }

                line = this.lines
                    .append('g')
                    .attr('class', `line line-${dataKey} ${hasFicBicStatus ? 'fic-bic-colored' : ''}`);

                let lineColor = lineColors[dataKey];

                if(hasFicBicStatus){
                    FicBicModule.drawData(this, data, dataKey, line, lineColor, userFicBicColors);
                } else {
                    data[dataKey].forEach((revenues, index) => {
                        if (revenues.length === 1) {
                            line
                                .append('circle')
                                .datum(revenues[0])
                                .attr('class', 'data-circle select')
                                .attr('r', 8)
                                .attr('fill', lineColor);

                            line
                                .append('circle')
                                .datum(revenues[0])
                                .attr('class', 'data-circle main')
                                .attr('r', 2)
                                .attr('fill', lineColor);
                        }

                        line
                            .append('path')
                            .attr('class', 'data-line select')
                            .attr('stroke', lineColor)
                            .datum(revenues);

                        line
                            .append('path')
                            .attr('class', 'data-line shadow')
                            .attr('stroke', lineColor)
                            .datum(revenues);

                        line
                            .append('path')
                            .attr('class', 'data-line main')
                            .attr('stroke', lineColor)
                            .attr('stroke-width', 2)
                            .datum(revenues);

                        revenues.forEach((rev) => {

                            if (typeof rev.stopped_at !== 'undefined' && rev.stopped_at) {
                                this.drawStoppedEvents(line, rev, dataKey);
                            }
                        })
                    });
                }

                paths = line
                    .selectAll('.data-line');

                circles = line
                    .selectAll('.data-circle');

                line
                    .on('click', () => {
                        d3.event.stopPropagation();
                        this.lineClickEvent(dataKey);
                    });

                paths
                    .on('mouseover', (d) => {
                        // disable tooltip if line is dim
                        const dataKey = Object.keys(self.data).find((key)=> Object.values(self.data[key]).find((e)=>e.id == d[0].id) !== undefined);
                        if (dataKey === undefined) return;

                        if(self.linesState[dataKey] == 'dim') return;

                        const svgBox = d3.select(`#${this.selector}`).select('svg').node().getBoundingClientRect();
                        let xScaleInvert;
                        if(svgBox && svgBox.x && svgBox.y){
                            xScaleInvert = this.xScale.invert(d3.event.x - svgBox.x - this.margins.left);
                        } else {
                            xScaleInvert = this.xScale.invert(d3.event.x - svgBox.left - this.margins.left);
                        }
                        const index = Math.round(xScaleInvert);

                        if(this.filters.startSyncView && index == 0) 
                            return; 

                        const singleData = this.filters.startSyncView  ? d[index] : d.find(datapoint => `${datapoint.year} ${datapoint.quarter}` ===  this.quarters[index]);

                        let position;
                        if(svgBox && svgBox.x && svgBox.y){
                            position = {
                                x: this.xScale(index) + svgBox.x + this.margins.left - 7,
                                y: this.yScale(singleData.revenue) + svgBox.y + this.margins.top,
                            };
                        }
                        else {

                            position = {
                                x: this.xScale(index) + svgBox.left + this.margins.left  -7,
                                y: this.yScale(singleData.revenue) + svgBox.top + this.margins.top,
                            };
                        }

                        this.tooltipMouseOver(position, singleData, []);
                        this.dataLineHover(singleData);
                    })
                    .on('mouseleave', () => {
                        this.tooltipMouseLeave();
                        this.dataLineHoverEnd();
                    });

                circles
                    .on('mouseover', (d) => {
                        // disable tooltip if line is dim
                        const dataKey = Object.keys(self.data).find((key)=> Object.values(self.data[key]).find((e)=>e.id == d.id) !== undefined);
                        if (dataKey === undefined) return;

                        if(self.linesState[dataKey] == 'dim') return;

                        const svgBox = d3.select(`#${this.selector}`).select('svg').node().getBoundingClientRect();
                        const index = this.quarters.findIndex(quarter => `${d.year} ${d.quarter}` === quarter);
                        let position;
                        if(svgBox && svgBox.x && svgBox.y){
                            position = {
                            x: this.xScale(index) + svgBox.x + this.margins.left - 7,
                            y: this.yScale(d.revenue) + svgBox.y + this.margins.top,
                            }
                        } else {
                            position = {
                                x: this.xScale(index) + svgBox.left + this.margins.left - 7,
                                y: this.yScale(d.revenue) + svgBox.top + this.margins.top,
                            }
                        }


                        this.tooltipMouseOver(position, d, []);
                        this.dataLineHover(d);
                    })
                    .on('mouseleave', () => {
                        this.tooltipMouseLeave();
                        this.dataLineHoverEnd();
                    });
            } else {
                line = this.lines.select(`.line-${dataKey}`);
                paths = line.selectAll('.data-line');
                circles = line.selectAll('.data-circle');
                stoppedDots = line.selectAll(`.stopped-dots g`);
            }

            this.drawEvents(line, dataKey, !hasLines, animate);

            const lineColor = lineColors[dataKey];

            LoeEventsModule.drawLoeEvents(this, line, dataKey, !hasLines || force, animate, lineColor);    
           
            if (animate) {
                paths = paths
                    .transition()
                    .duration(500);

                circles = circles
                    .transition()
                    .duration(500);

                if (stoppedDots){
                    stoppedDots = stoppedDots
                        .transition()
                        .duration(500);
                }
            }

            paths.attr('d', this.lineFunction);
            circles
                .attr('cx', d => this.xScale(this.getQuarterIndex(d)))
                .attr('cy', d => this.yScale(d.revenue));

            if (stoppedDots) {
                stoppedDots
                    .attr('transform', (d) => {
                        const translateX = this.xScale(this.getQuarterIndex(d)) - 7;
                        const translateY = this.yScale(d.revenue) - 7;

                        return `translate(${translateX}, ${translateY})`;
                    });
            }
        });
        
        if(this.showCumulativeRevenue)
            this.drawCumulativeData(cumulativeData,hasLines,animate)
    }

    drawCumulativeData(cumulativeData,hasLines,animate){
        let cumulativeLine;
        let cumulativePaths;
        let cumulativeCircles;
        let cumulativeStoppedDots;
        const cumulativeLineColor = 'black';
        const self = this;


        if (!hasLines) {
            cumulativeLine = this.lines
                .append('g')
                .attr('class', `line line-cumulative`)
                .attr('stroke', cumulativeLineColor);

            if (cumulativeData.length === 1) {
                cumulativeLine
                    .append('circle')
                    .datum(cumulativeData[0])
                    .attr('class', 'data-circle select')
                    .attr('r', 8)
                    .attr('fill', cumulativeLineColor);

                cumulativeLine
                    .append('circle')
                    .datum(cumulativeData[0])
                    .attr('class', 'data-circle main')
                    .attr('r', 2)
                    .attr('fill', cumulativeLineColor);
            }

            cumulativeLine
                .append('path')
                .attr('class', 'data-line select')
                .attr('stroke', cumulativeLineColor)
                .datum(cumulativeData);

            cumulativeLine
                .append('path')
                .attr('class', 'data-line main')
                .attr('stroke-dasharray', '5,5')
                .attr('stroke-width', 2)
                .datum(cumulativeData);

            cumulativePaths = cumulativeLine
                .selectAll('.data-line');

            cumulativeCircles = cumulativeLine
                .selectAll('.data-circle');

            cumulativeLine
                .on('click', () => {
                    d3.event.stopPropagation();
                    this.lineClickEvent('cumulative');
                });
            cumulativePaths
                .on('mouseover', (d) => {
                    // disable tooltip if line is dim
                    if(self.linesState['cumulative'] == 'dim') return;

                    const svgBox = d3.select(`#${this.selector}`).select('svg').node().getBoundingClientRect();
                    let xScaleInvert;
                    if(svgBox && svgBox.x && svgBox.y){
                        xScaleInvert = this.xScale.invert(d3.event.x - svgBox.x - this.margins.left);
                    } else {
                        xScaleInvert = this.xScale.invert(d3.event.x - svgBox.left - this.margins.left);
                    }
                    const index = Math.round(xScaleInvert);

                    const quarter = this.quarters[index];
                    const singleData = d.find(datapoint => `${datapoint.year} ${datapoint.quarter}` === quarter);
                    if(!singleData) return;

                    let position;
                    if(svgBox && svgBox.x && svgBox.y){
                        position = {
                            x: this.xScale(index) + svgBox.x + this.margins.left - 7,
                            y: this.yCumulativeScale(singleData.revenue) + svgBox.y + this.margins.top,
                        };
                    }
                    else {

                        position = {
                            x: this.xScale(index) + svgBox.left + this.margins.left  -7,
                            y: this.yCumulativeScale(singleData.revenue) + svgBox.top + this.margins.top,
                        };
                    }

                    this.tooltipMouseOver(position, singleData, [], false, true);
                    this.dataLineHover(singleData, true);
                })
                .on('mouseleave', () => {
                    this.tooltipMouseLeave();
                    this.dataLineHoverEnd();
                });

            cumulativeCircles
                .on('mouseover', (d) => {
                    // disable tooltip if line is dim
                    if(self.linesState['cumulative'] == 'dim') return;

                    const svgBox = d3.select(`#${this.selector}`).select('svg').node().getBoundingClientRect();
                    const index = this.quarters.findIndex(quarter => `${d.year} ${d.quarter}` === quarter);
                    let position;
                    if(svgBox && svgBox.x && svgBox.y){
                        position = {
                        x: this.xScale(index) + svgBox.x + this.margins.left - 7,
                        y: this.yCumulativeScale(d.revenue) + svgBox.y + this.margins.top,
                        }
                    } else {
                        position = {
                            x: this.xScale(index) + svgBox.left + this.margins.left - 7,
                            y: this.yCumulativeScale(d.revenue) + svgBox.top + this.margins.top,
                        }
                    }

                    this.tooltipMouseOver(position, d, [], false, true);
                    this.dataLineHover(d, true);
                })
                .on('mouseleave', () => {
                    this.tooltipMouseLeave();
                    this.dataLineHoverEnd();
                });
        } else {
            cumulativeLine = this.lines.select(`.line-cumulative`);
            cumulativePaths = cumulativeLine.selectAll('.data-line');
            cumulativeCircles = cumulativeLine.selectAll('.data-circle');
            cumulativeStoppedDots = cumulativeLine.selectAll(`.stopped-dots g`);
        }

        if (animate) {
            cumulativePaths = cumulativePaths
                .transition()
                .duration(500);

                cumulativeCircles = cumulativeCircles
                .transition()
                .duration(500);

            if (cumulativeStoppedDots){
                cumulativeStoppedDots = cumulativeStoppedDots
                    .transition()
                    .duration(500);
            }
        }

        cumulativePaths.attr('d', this.cumulativeLineFunction);
        cumulativeCircles
            .attr('cx', d => this.xScale(this.getQuarterIndex(d)))
            .attr('cy', d => this.yCumulativeScale(d.revenue));

        if (cumulativeStoppedDots) {
            cumulativeStoppedDots
                .attr('transform', (d) => {
                    const translateX = this.xScale(this.getQuarterIndex(d)) - 7;
                    const translateY = this.yCumulativeScale(d.revenue) - 7;

                    return `translate(${translateX}, ${translateY})`;
                });
        }
    }

    tooltipMouseOver(position, data, events,showIndividualEvent, isCumulativeRevenueCurve = false) {

        if(this.cumulative !== 'none' && this.cumulative !== '' && this.cumulative !== null && !isCumulativeRevenueCurve){
            //this a cumulative chart so skip normal tooltip
            this.tooltipCumulativeMouseOver(position, data);
            return
        }

        //below check is to show tooltip only to active curve if available.
        if(this.activeLine && ((data.drug_brand_id &&
            this.activeLine !== data.drug_brand_id+'_'+data.company_id) ||
            (data.key && this.activeLine !== data.key))){
            return;
        }
        const tooltip = d3.select(`#${this.selector}-wrapper`).select('.chart-tooltip');
        const tooltipArrow = tooltip.select('.tooltip-arrow');
        let title;
        tooltip.select('h4').style('text-align','left');
        tooltip.select('.divider').style('display','');
        if (this.type === 'growth') {
            title = data.drugBrands.reduce((total, drugBrand, index) => {
                if (index === 0) {
                    return drugBrand.brand_name;
                }

                return `${total} <br/> ${drugBrand.brand_name}`;
            }, '');
        } else {
            title = data.drug_brand_name ? data.drug_brand_name.brand_name : data.brand_name;
        }

        const tooltipTitle = isCumulativeRevenueCurve ? 'Cumulative Revenue' : title;
        tooltip.select('h4').html(tooltipTitle);

        const text = tooltip.select('.event-text');
        text.selectAll('p').remove();
        text.selectAll('div').remove();
        tooltip.select('.event-revenue').style('display','none');
        tooltip.select('.event-quarter').style('display','none');
        if(showIndividualEvent){
            if (data.stopped_at !== "undefined" && data.stopped_at){
                text.append('p').attr('class', 'event-type')
                    .html('Last Disclosed Revenue:');

                text.append('p').attr('class', 'event-revenue')
                    .html(`$ ${parseFloat(data.revenue).toFixed(2)} M revenue`);

                text.append('p').attr('class', 'event-quarter')
                    .html(`${data.year} ${data.quarter}`);

                text.append('p').attr('class', 'event-info')
                    .html('This is the last quarter with disclosed revenues.');
            }else {
                if(data.type === 'loe'){
                   LoeEventsModule.updateTooltipInfo(text, data)
                }else{
                    let eventTitle = `New ${data.type}`;
                    let marketName = '';

                    if (data.type === 'indication') {
                        marketName = `(${data.market})`;
                    }

                    if (data.actual_brand_name !== title) {
                        eventTitle = `${eventTitle} (${data.actual_brand_name}) ${marketName}:`;
                    }else {
                        eventTitle = `${eventTitle} ${marketName}`;
                    }

                    text.append('p').attr('class', 'event-type')
                        .html(eventTitle);

                    text.append('p').attr('class', 'event-name')
                        .html(data.name);

                    text.append('p').attr('class', 'event-date')
                        .html(moment(data.date).format('MMM DD YYYY'));
                }
            }

        } else {
            tooltip.select('.event-revenue').style('display','');
            tooltip.select('.event-quarter').style('display','');
            events.forEach((quarterEvent) => {
                let eventTitle = `New ${quarterEvent.type}`;
                let marketName = '';

                if (quarterEvent.type === 'indication') {
                    marketName = `(${quarterEvent.market})`;
                }

                if (quarterEvent.actual_brand_name !== title) {
                    eventTitle = `${eventTitle} (${quarterEvent.actual_brand_name}) ${marketName}:`;
                }else {
                    eventTitle = `${eventTitle} ${marketName}`;
                }

                text.append('p').attr('class', 'event-type')
                    .html(eventTitle);

                text.append('p').attr('class', 'event-name')
                    .html(quarterEvent.name);

                text.append('p').attr('class', 'event-date')
                    .html(moment(quarterEvent.date).format('MMM DD YYYY'));
            });

            tooltip.select('.event-revenue').html(`$ ${parseFloat(data.revenue).toFixed(2)} M revenue`);
            tooltip.select('.event-quarter').html(`${data.year} ${data.quarter}`);
            if (events.length > 0) {
                text.append('div').attr('class', 'divider');
            }
        }

        const selector = document.querySelector(`#${this.selector}-wrapper .tooltip`);
        const tooltipPosition = this.getTooltipPosition(position, selector);

        if (tooltipPosition.outOnRight) {
            tooltip.classed('right', false);
            tooltip.classed('left', true);
        }

        if (tooltipPosition.outOnTop) {
            const tooltipArrowPos = (position.y || position.top) - 20;
            tooltipArrow.attr('style', `top: ${tooltipArrowPos}px`)
        }

        if (tooltipPosition.outOnBottom) {
            const tooltipArrowPos = (position.y || position.top) - tooltipPosition.top
                + (tooltipPosition.tooltip_rect.height / 2);
            tooltipArrow.attr('style', `top: ${tooltipArrowPos}px`)
        }

        tooltip.attr('style', `top: ${tooltipPosition.top}px; left: ${tooltipPosition.left}px;`);
        tooltip.classed('closed', false);
    }

    tooltipCumulativeMouseOver(position, data) {

        let cumulativeRevenues = data.items;

        const tooltip = d3.select(`#${this.selector}-wrapper`).select('.chart-tooltip');
        const tooltipArrow = tooltip.select('.tooltip-arrow');
        let title;
        tooltip.select('h4').style('text-align', 'left');
        tooltip.select('.divider').style('display', '');

        title = data.drug_brand_name.brand_name;
        tooltip.select('h4').html(title);

        const text = tooltip.select('.event-text');
        text.selectAll('p').remove();
        text.selectAll('div').remove();
        tooltip.select('.event-revenue').style('display', 'none');
        tooltip.select('.event-quarter').style('display', 'none');
        tooltip.select('.event-revenue').style('display', '');
        tooltip.select('.event-quarter').style('display', '');

        // Sort the resulting brands by redevnue DESC
        cumulativeRevenues.sort((a,b) => (parseFloat(a.revenue) < parseFloat(b.revenue)) ? 1 : ((parseFloat(b.revenue) < parseFloat(a.revenue)) ? -1 : 0));


        cumulativeRevenues.forEach((cumulativeRevenue) => {
            let eventTitle = `${cumulativeRevenue.drug_brand_name.brand_name.toUpperCase()}`+ ' '+ `$ ${parseFloat(cumulativeRevenue.revenue).toFixed(2)} M `;

            text.append('p').attr('class', 'event-type')
                .html(eventTitle);

        });
        text.append('div').attr('class', 'divider');

        tooltip.select('.event-revenue').html(`$ ${parseFloat(data.revenue).toFixed(2)} M revenue`);
        tooltip.select('.event-quarter').html(`${data.year} ${data.quarter}`);

        const selector = document.querySelector(`#${this.selector}-wrapper .tooltip`);
        const tooltipPosition = this.getTooltipPosition(position, selector);

        if (tooltipPosition.outOnRight) {
            tooltip.classed('right', false);
            tooltip.classed('left', true);
        }

        if (tooltipPosition.outOnTop) {
            const tooltipArrowPos = (position.y || position.top) - 20;
            tooltipArrow.attr('style', `top: ${tooltipArrowPos}px`)
        }

        if (tooltipPosition.outOnBottom) {
            const tooltipArrowPos = (position.y || position.top) - tooltipPosition.top
                + (tooltipPosition.tooltip_rect.height / 2);
            tooltipArrow.attr('style', `top: ${tooltipArrowPos}px`)
        }

        tooltip.attr('style', `top: ${tooltipPosition.top}px; left: ${tooltipPosition.left}px;`);

        tooltip.classed('closed', false);
    }

    tooltipMouseLeave() {
        const tooltip = d3.select(`#${this.selector}-wrapper`).select('.chart-tooltip');
        const tooltipArrow = tooltip.select('.tooltip-arrow');

        tooltipArrow.attr('style', null);
        tooltip.classed('closed', true);
        tooltip.classed('top', false);
        tooltip.classed('right', true);
        tooltip.classed('bottom', false);
        tooltip.classed('left', false);
    }

    getTooltipPosition(position, tooltip) {

        const tooltip_rect = tooltip.getBoundingClientRect();

        const _position = {
            top: 0,
            left: 0
        };

        const attributes = {
            outOnTop: false,
            outOnRight: false,
            outOnBottom: false
        };

        if (typeof position === "undefined") return _position;

        if(position && position.x && position.y) {
            _position.top = position.y;
            _position.left = position.x + 10;
        } else {
            _position.top = position.top;
            _position.left = position.left + 10;
        }

        // Out on the right
        if (tooltip_rect.width + _position.left > (window.innerWidth || document.documentElement.clientWidth)){
            attributes.outOnRight = true;
            _position.left -= 10;
        }

        // Out on the top
        if ((tooltip_rect.height / 2) > _position.top) {
            attributes.outOnTop = true;
            _position.top += ((tooltip_rect.height / 2) - _position.top) + 20;
        }

        // Out on the bottom
        const bottomHandler = (tooltip_rect.height / 2) + _position.top;
        if ( bottomHandler > (window.innerHeight || document.documentElement.clientHeight)){
            attributes.outOnBottom = true;
            _position.top -= bottomHandler - (window.innerHeight || document.documentElement.clientHeight) + 20;
        }

        return {..._position, ...attributes, ...{tooltip_rect}};
    }

    drawStoppedEvents(line, rev, dataKey) {
        let stoppedDot;

        stoppedDot = line
            .append('g')
            .attr('class', `stopped-dots stopped-dots-${dataKey}`);

        stoppedDot
            .append(() =>  this.stoppedIcon.node().cloneNode(true))
            .datum(rev)
            .attr('transform', (d) => {
                const translateX = this.xScale(this.getQuarterIndex(d)) - 7;
                const translateY = this.yScale(d.revenue) - 7;

                return `translate(${translateX}, ${translateY})`;
            })
            .on('mouseover', (data) => {
                d3.event.stopPropagation();
                const svgBox = d3.select(`#${this.selector}`).select('svg').node().getBoundingClientRect();
                const xScaleInvert = this.xScale.invert(d3.event.x - svgBox.x - this.margins.left);
                const index = Math.round(xScaleInvert);
                let position;
                if(svgBox && svgBox.x && svgBox.y){
                        position= {
                        x: this.xScale(index) + svgBox.x + this.margins.left - 7,
                        y: this.yScale(data.revenue) + svgBox.y + this.margins.top,
                    };
                } else {
                    position= {
                        x: this.xScale(index) + svgBox.top + this.margins.left - 7,
                        y: this.yScale(data.revenue) + svgBox.left + this.margins.top,
                    };
                }

                this.tooltipMouseOver(position, data, [], true);
                this.dataLineHover(data);
            })
            .on('mouseleave', () => {
                this.tooltipMouseLeave();
                this.dataLineHoverEnd();
            });
    }

    drawEvents(line, dataKey, isNew, animate) {
        const self = this;
        this.eventDateMap = new Map;
        if (this.events[dataKey]) {

            if (isNew) {
                const events = [...this.events[dataKey].indications, ...this.events[dataKey].markets];
                const eventsPerQuarter = {};
                events.forEach((event) => {
                    if (event) {
                        const quarter = self.filters.startSyncView ? event.indexedQuarter :  `${event.year}_${event.quarter}`;

                        if (!eventsPerQuarter[quarter]) {
                            eventsPerQuarter[quarter] = [event];
                        } else {
                            eventsPerQuarter[quarter].push(event);
                        }
                    }
                });

                const that = this;
                // console.log(events);
                // events.sort((a, b) =>{
                //     if(a.revenue === null){
                //         return 1;
                //     }
                //     if(b.revenue === null){
                //         return -1;
                //     }
                //     if (a.revenue > b.revenue)
                //       return -1;
                //     if (a.revenue < b.revenue)
                //       return 1;
                //     return 0;
                //   });
                //   console.log(events);
                events.forEach(async (event) => {
                    if (isNew && event && event.revenue) {
                        const icon = event.type === 'market' ? this.marketIcon : this.indicationIcon;

                        line
                            .append(() => icon.node().cloneNode(true))
                            .datum(event)
                            .attr('class', `event event-${event.type}`)
                            .attr('transform', (d) => {
                                // const translateX = this.xScale(this.getQuarterIndex(d)) - 7;
                                // const translateY = this.yScale(d.revenue) - 7;

                                return this.getTranslateXandY(d);
                            })
                            .on('mouseover', function mouseOver(d) {
                                const position = d3.select(this).node().getBoundingClientRect();
                                const quarter = that.filters.startSyncView ? d.indexedQuarter : `${d.year}_${d.quarter}`;
                                const quarterEvents = eventsPerQuarter[quarter];
                                that.tooltipMouseOver(position, d, quarterEvents,true);
                                if (that.isEventInQuarter(d)) that.dataLineHover(d);
                            })
                            .on('mouseleave', () => {
                                this.tooltipMouseLeave();
                                this.dataLineHoverEnd();
                            });
                    }
                });
            } else {
                let circles = line.selectAll('.event:not(.event.event-loe)');

                if (animate) {
                    circles = circles
                        .transition()
                        .duration(500);
                }

                circles
                    .attr('transform', (d) => {
                        // const translateX = this.xScale(this.getQuarterIndex(d)) - 7;
                        // const translateY = this.yScale(d.revenue) - 7;

                        return this.getTranslateXandY(d);
                    });
            }
        }
    }

    /**
     *
     * @param d
     * @returns {boolean}
     * Check if the event date is same as revenue quarter date, the value is determined by the difference
     * of days between quarters
     */
    isEventInQuarter(d) {

        if (typeof d === "undefined"
            || !d
            || typeof d.date === "undefined") return false;

        const eventDate = new Date(d.date),
            quarter = Math.floor((eventDate.getMonth() / 3)),
            firstDate = new Date(eventDate.getFullYear(), quarter * 3, 1);

        return (eventDate.setHours(0,0,0,0).valueOf() === firstDate.setHours(0,0,0,0).valueOf());
    }

    getTranslateXandY(d) {
        const eventDate = new Date(d.date);
        var quarter = Math.floor((eventDate.getMonth() / 3));
        var firstDate = new Date(eventDate.getFullYear(), quarter * 3, 1);
        var endDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0);

        const days  = new Date(endDate - firstDate)/1000/60/60/24;
        const eventDateDIff  = new Date(eventDate - firstDate)/1000/60/60/24;
        const datePercentage =eventDateDIff*100/days;
        const totalQuaterDistance = this.xScale(this.getQuarterIndex(d)+1)-this.xScale(this.getQuarterIndex(d));
        const ploatX = datePercentage*totalQuaterDistance / 100;

        const translateX = this.xScale(this.getQuarterIndex(d)) -7+ploatX;

        let currentQuarter = Math.ceil((eventDate.getMonth()+1) / 3);
        let yearToGet = currentQuarter === 4 ? eventDate.getFullYear() + 1 : eventDate.getFullYear();
        let quarterToGet = currentQuarter === 4 ? 1 : currentQuarter + 1;
        let nextRevItem = this.filters.startSyncView ? this.data[d.key][d.indexedQuarter + 1] : this.data[d.key].find(a => a.year === yearToGet && a.quarter === "Q"+quarterToGet.toString())
        let revenue = nextRevItem?nextRevItem.revenue:d.revenue;
        let revDiff = revenue-d.revenue;

        if(this.eventDateMap.has(d.date+d.key) && d.revenue){
            let add = parseInt(this.eventDateMap.get(d.date+d.key))-14;
            this.eventDateMap.delete(d.date+d.key);
            this.eventDateMap.set(d.date+d.key,add+"")
            const translateY = add;
            return `translate(${translateX}, ${translateY})`;
        } else {

            const translateY = this.yScale(d.revenue*1 + (datePercentage/100*revDiff)) - 7;
            this.eventDateMap.set(d.date+d.key,translateY);
            return `translate(${translateX}, ${translateY})`;
        }

    }

    getLineColorAtDate(dataKey, date, defaultColor){
        if(!this.colorByFicBic){
            return defaultColor;
        }

        const ficBicStatus = FicBicModule.getFicBicStatus(
            this,
            this.data[dataKey][0].drug_brand_name,
            date
        );

        return FicBicModule.getStateColor(ficBicStatus, defaultColor, this.getUserFicBicColors());
    }

    drawSvg() {
        this.clearDrawing();

        const rootElement = document.getElementById(this.selector);
        const width = rootElement.offsetWidth - this.margins.left - this.margins.right;
        const height = rootElement.offsetHeight - this.margins.top - this.margins.bottom;

        const svg = d3.select(`#${this.selector}`)
            .append('svg')
            .attr('class', 'svg')
            .attr('width', rootElement.offsetWidth)
            .attr('height', rootElement.offsetHeight)
            .append('g')
            .attr('class', 'chart-wrapper')
            .attr('width', rootElement.offsetWidth)
            .attr('height', rootElement.offsetHeight)
            .attr('transform', `translate(${this.margins.left}, ${this.margins.top})`);

        svg.append('clipPath')
            .attr('id', `clip-${this.selector}`)
            .append('rect')
            .attr('width', width)
            .attr('height', height);

        this.svg = {
            element: svg,
            width,
            height,
        };
    }

    drawBackground() {
        //user_id should be replaced with actual user_id
        var user_id = localStorage.getItem('uid');
        user_id = parseInt(atob(user_id));

        //tmp=2^(n-1) where n is the number of bits used for coding the user_id
        var n = 16
        var module = n / 2 + 1
        var tmp = 1
        for (var i = 1; i < n; i++) {
            tmp *= 2;
        }
        var array1 = new Array(module - 1).fill(0);
        var array2 = new Array(module - 1).fill(0);

        for (var i = 1; i <module; i++) {
            if (user_id >= tmp) {
                user_id -= tmp;
                array1[i] = 1;
            }
            tmp /= 2;
        }
        for (var i = 1; i <module; i++) {
            if (user_id >= tmp) {
                user_id -= tmp;
                array2[i] = 1;
            }
            tmp /= 2;
        }

        var index = 0
        var radius = 10
        var even_line = true
        var even_block = true
        for (var i = 5; i <= this.svg.height + 50; i += 25) {
            for (var j = 5; j <= this.svg.width + 50; j += 25) {
                if ((even_block && index % module == 0) || (even_line && array1[index % module]) || (!even_line && array2[index % module])) {
                    this.svg.element.append("circle")
                        .attr("cx", j)
                        .attr("cy", i)
                        .attr("r", radius)
                        .style("fill", "rgb(255, 255, 251)");

                }
                index++;
            }
            index = 0;
            even_line = (even_line == true) ? false : true;
            even_block = (even_line == true) ? !even_block : even_block;
        }
    }



    drawGraph() {
        this.setDefaultDomain();
        this.drawSvg();
        this.drawBackground();
        this.setScale();
        this.drawAxes();
        this.drawData();
        this.bindEvents();
        this.updateStoppedDotsVisibility(this.showStopIcon);
        LoeEventsModule.updateLoeEventsVisibility(this);
        this.handleLinesStateUpdate();
    }

    clearDrawing() {
        const rootElement = document.getElementById(this.selector);

        if (rootElement && rootElement.firstChild) {
            while (rootElement.firstChild) {
                rootElement.removeChild(rootElement.firstChild);
            }
        }

        this.lineClickEvent();
    }

    resize() {
        const rootElement = document.getElementById(this.selector);
        if(!rootElement) return;
        const width = rootElement.offsetWidth - this.margins.left - this.margins.right;
        const height = rootElement.offsetHeight - this.margins.top - this.margins.bottom;

        d3.select(`#${this.selector}`)
            .select('.svg')
            .attr('width', rootElement.offsetWidth)
            .attr('height', rootElement.offsetHeight)
            .select('.chart-wrapper')
            .attr('width', rootElement.offsetWidth)
            .attr('height', rootElement.offsetHeight);

        this.svg.element
            .select(`#clip-${this.selector}`)
            .select('rect')
            .attr('width', width)
            .attr('height', height);

        this.svg.width = width;
        this.svg.height = height;

        this.yDraggerResize();
        this.redraw();
    }

    yDraggerResize() {

        d3.select(`#${this.selector}`)
            .select('.svg')
            .select('.chart-wrapper')
            .select('.vertical-dragger')
            .attr('x', this.svg.width)
            .attr('height', this.svg.height);
    }

    reset() {
        this.setDefaultDomain(true);
        this.redraw(true);
    }

    redraw(animate) {
        this.setScale();
        this.drawAxes(animate);
        this.drawData(animate);
    }

    forceRedraw(updatedQuarters){
        const currentXDomain = this.xDomain;
        
        const currentMinQuarter = this.quarters[currentXDomain[0]];
        const currentHighQuarter = this.quarters[currentXDomain[1]];

        if(updatedQuarters){
            this.quarters = updatedQuarters;
        }

        let minQuarterIndex =  this.quarters.findIndex((quarter)=> quarter == currentMinQuarter);
        let highQuarterIndex =  this.quarters.findIndex((quarter)=> quarter == currentHighQuarter);

        const currentLiensState = JSON.parse(JSON.stringify(this.linesState));

        this.setDefaultDomain();
        this.xDomain = [ minQuarterIndex, highQuarterIndex];
        this.setScale();
        this.drawSvg();
        this.drawBackground();
        this.lines = null;
        this.drawAxes();
        this.drawData(true);
        this.bindEvents();

        // fix svg ox lines not showing properly 
        this.resize();

        this.linesState = currentLiensState;

        this.updateStoppedDotsVisibility(this.showStopIcon);
        LoeEventsModule.updateLoeEventsVisibility(this);
        this.handleLinesStateUpdate();
    }

    updateStoppedDotsVisibility(showStopIcon){

        if(showStopIcon){
         d3.selectAll(`.stopped-dots g`).style('display','');

        }else{
         d3.selectAll(`.stopped-dots g`).style('display','none');
        }
    }
}

export default BaseDrawingService;
